import { Injectable } from '@angular/core';
import {
    ChapterType,
    ICellChangeResponse,
    IQuotationBlock,
    IQuotationCost,
    IQuotationSection,
    IQuotationsQuotation,
    IQuotationTraveller
} from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { ITableHeaders, ITableRow, TableRowType } from "app_quotation/shared/services/table-data/ITable";
import { Yii } from "libraries/interfaces/Yii";
import {
    BLOCK_PLACEHOLDER,
    feesChapterHeader,
    servicesChapterHeader,
    TableDataService,
    tripChapterHeader
} from "app_quotation/shared/services/table-data/table-data.service";

import { EQuotationEntryPoints } from "libraries/enums/e-quotation-entry-points";

const EMPTY_FEES: ITableRow = {
    chapterType: ChapterType.fees,
    block: BLOCK_PLACEHOLDER,
    type: TableRowType.blockData,
};
const EMPTY_SERVICES: ITableRow = {
    chapterType: ChapterType.services,
    block: BLOCK_PLACEHOLDER,
    type: TableRowType.blockData,
};
const EMPTY_TRIP: ITableRow = {
    chapterType: ChapterType.trip,
    block: BLOCK_PLACEHOLDER,
    type: TableRowType.blockData,
};

@Injectable()
export class CostTableDataService {

    constructor(
        private readonly tableDataService: TableDataService) {
    }

    public computeTableData(quotation: IQuotationsQuotation, subPage: EQuotationEntryPoints): ITableRow[] {
        let total: IQuotationCost;
        if (this.tableDataService.hasCost(quotation)) {
            total = {
                amount: quotation.cost.amount,
                currency: quotation.cost.currency,
            };
        }

        const valueHeader: ITableRow =
            {
                title: Yii.t('wui-quotation-js', "Prix d'achat total inclus dans cette grille"),
                total,
                type: TableRowType.totalHeader,
                travellers: extractValueHeaderTravellers(quotation),
            };

        switch (subPage) {
            case EQuotationEntryPoints.option:
                return [
                    tripChapterHeader,
                    ...this.extractBlocksFromSection(quotation, ChapterType.trip, subPage),
                    servicesChapterHeader,
                    ...this.extractBlocksFromSection(quotation, ChapterType.services, subPage),
                ];

            case EQuotationEntryPoints.included:
            default:
                return [
                    valueHeader,
                    tripChapterHeader,
                    ...this.extractBlocksFromSection(quotation, ChapterType.trip, subPage),
                    ...getLandQuoteBlockPlaceholder(quotation),
                    feesChapterHeader,
                    ...this.extractBlocksFromSection(quotation, ChapterType.fees, subPage),
                    servicesChapterHeader,
                    ...this.extractBlocksFromSection(quotation, ChapterType.services, subPage),
                ];
        }

    }

    public computeTableHeaders(quotation: IQuotationsQuotation, subPage: EQuotationEntryPoints): ITableHeaders {

        let title: string;

        switch (subPage) {
            case EQuotationEntryPoints.option:
                title = Yii.t("wui-quotation-js", "Prix d'achat en option");
                break;
            case EQuotationEntryPoints.included:
            default:
                title = Yii.t("wui-quotation-js", "Prix d'achat inclus");
                break;
        }


        return {
            title,
            total: Yii.t("wui-quotation-js", "Total Achat"),
            travellers: this.tableDataService.extractHeaderTravellers(quotation),
        };
    }

    public getTableAfterAddPriceBlockPlaceholder(
        currentTableData: ITableRow[],
        {block, travellers, chapterType, ...previousElement}: ITableRow)
        : ITableRow[] {
        const emptyTableRow: ITableRow = {
            type: TableRowType.blockData,
            chapterType,
            block: {
                ...BLOCK_PLACEHOLDER,
                isFirstSectionBlock: false,
                section: block.section,
                order: block.order + 1
            },
            travellers,
        };
        const costTable: ITableRow[] = currentTableData
            .filter((costRow: ITableRow) =>
                !costRow.block ||
                Boolean(
                    costRow.block &&
                    !costRow.block.isEmpty ||
                    (costRow.block.isEmpty && costRow.chapterType !== chapterType)
                )
            );
        const placeholderIndex: number = costTable
            .findIndex((costRow: ITableRow) => costRow.block && costRow.block.id === block.id) + 1;
        costTable.splice(placeholderIndex, 0, emptyTableRow);
        const newCostTable: ITableRow[] = resetCostBlockAttributes(costTable);
        return newCostTable;
    }

    public getTableAfterCreatePriceBlock(currentTableData: ITableRow[], newBlock: IQuotationBlock, chapterCode: ChapterType): ITableRow[] {
        const costTable: ITableRow[] = currentTableData
            .map((costRow: ITableRow) => {
                if (costRow.block &&
                    Boolean(costRow.block &&
                        costRow.block.isEmpty &&
                        costRow.chapterType === chapterCode
                    )
                ) {
                    return {
                        ...costRow,
                        block: {
                            ...costRow.block,
                            ...newBlock,
                            isEmpty: false,
                        }
                    };
                }
                return costRow;
            });
        return resetCostBlockAttributes(costTable);
    }

    public getTableAfterRemovePriceBlock(currentTableData: ITableRow[], cellChangeResponse: ICellChangeResponse, quotation: IQuotationsQuotation): ITableRow[] {
        let sectionId: number;
        const costTable: ITableRow[] = currentTableData
            .filter((costRow: ITableRow) => {
                const isAllowed: boolean = !costRow.block || Boolean(costRow.block && costRow.block.id !== cellChangeResponse.blockId);
                if (!isAllowed) {
                    sectionId = costRow.block.section;
                }
                return isAllowed;
            });
        if (!hasTripLandRow(costTable)) {
            const lastTripRowIndex: number = costTable
                .reduce((acc: number, costRow: ITableRow, index: number) => {
                    if (costRow.chapterType === ChapterType.trip && costRow.block && costRow.block.beginOffset) {
                        return index;
                    }
                    return acc;
                }, -1);
            const placeholder: ITableRow = {
                ...EMPTY_TRIP,
                block: {
                    ...EMPTY_TRIP.block,
                    section: sectionId
                },
                travellers: extractBlockTravellers(quotation),
            };
            costTable.splice(lastTripRowIndex + 1, 0, placeholder);
        }
        if (hasOnlyFeeChapterRow(costTable)) {
            const lastFeeRowIndex: number = costTable
                .reduce((acc: number, costRow: ITableRow, index: number) => {
                    if (costRow.chapterType === ChapterType.fees) {
                        return index;
                    }
                    return acc;
                }, -1);
            const placeholder: ITableRow = {
                ...EMPTY_FEES,
                block: {
                    ...EMPTY_FEES.block,
                    section: sectionId
                },
                travellers: extractBlockTravellers(quotation),
            };
            costTable.splice(lastFeeRowIndex + 1, 0, placeholder);
        }

        if (hasOnlyServiceChapterRow(costTable)) {
            const lastServiceRowIndex: number = costTable
                .reduce((acc: number, costRow: ITableRow, index: number) => {
                    if (costRow.chapterType === ChapterType.services) {
                        return index;
                    }
                    return acc;
                }, -1);
            const placeholder: ITableRow = {
                ...EMPTY_SERVICES,
                block: {
                    ...EMPTY_SERVICES.block,
                    section: sectionId
                }
            };
            costTable.splice(lastServiceRowIndex + 1, 0, placeholder);
        }



        return resetCostBlockAttributes(costTable);
    }

    public getSectionId(quotation: IQuotationsQuotation, chapterCode: ChapterType): number {
        return getSectionId(quotation, chapterCode);
    }

    private extractBlocksFromSection(quotation: IQuotationsQuotation, chapterType: ChapterType, subPage: EQuotationEntryPoints): ITableRow[] {
        const costTableRow: ITableRow[] = this.tableDataService.extractBlocksFromSection(quotation, chapterType, subPage);

        if (!costTableRow.length) {
            switch (chapterType) {
                case ChapterType.fees:
                    return [{
                        ...EMPTY_FEES,
                        travellers: extractBlockTravellers(quotation),
                    }];
                case ChapterType.services:
                    return [{
                        ...EMPTY_SERVICES,
                        travellers: extractBlockTravellers(quotation),
                    }];

                case ChapterType.trip:
                default:
                    return [];
            }
        }
        return costTableRow;
    }
}

function getSectionId(quotation: IQuotationsQuotation, chapterType: ChapterType): number {
    const landSection: IQuotationSection = (quotation.sections || [])
        .find((section: IQuotationSection) => {
            return section.chapterCode === chapterType &&
                !section.beginOffset;
        });
    return landSection ? landSection.id : null;
}

function resetCostBlockAttributes(currentTable: ITableRow[]): ITableRow[] {
    return currentTable.map((costRow: ITableRow) => {
        if (costRow.block) {
            const sectionBlocks: ITableRow[] = currentTable
                .filter((costRowItem: ITableRow) =>
                    costRowItem.chapterType === costRow.chapterType &&
                    costRowItem.block &&
                    costRowItem.block.section === costRow.block.section
                );
            costRow.block.sectionLength = sectionBlocks.length;
            costRow.block.isFirstSectionBlock = sectionBlocks[0].block.id === costRow.block.id;
        }
        return costRow;
    });
}

function hasTripLandQuoteBlock(quotation: IQuotationsQuotation): boolean {
    return (quotation.sections || [])
        .some((section: IQuotationSection) => {
            return section.chapterCode === ChapterType.trip &&
                !section.beginOffset &&
                section.blocks && section.blocks.length > 0;
        });
}

function hasTripLandRow(rows: ITableRow[]): boolean {
    return (rows || [])
        .some((row: ITableRow) => {
            return row.chapterType === ChapterType.trip &&
                row.block && !row.block.beginOffset;
        });
}
function hasOnlyFeeChapterRow(rows: ITableRow[]): boolean {
    return (rows || [])
        .filter((row: ITableRow) => row.chapterType === ChapterType.fees)
        .length === 1;
}
function hasOnlyServiceChapterRow(rows: ITableRow[]): boolean {
    return (rows || [])
        .filter((row: ITableRow) => row.chapterType === ChapterType.services)
        .length === 1;
}

function getLandQuoteBlockPlaceholder(quotation: IQuotationsQuotation): ITableRow[] {
    if (!hasTripLandQuoteBlock(quotation)) {
        return [{
            ...EMPTY_TRIP,
            block: {
                ...EMPTY_TRIP.block,
                section: getSectionId(quotation, ChapterType.trip)
            },
            travellers: extractBlockTravellers(quotation),
        }];
    }
    return [];
}

function extractBlockTravellers(quotation: IQuotationsQuotation): IQuotationTraveller[] {
    //tempo, will evolve to get quotation.blocks[0].prices[0].details[0].travellers[0].cost when possible
    return (quotation.travellers || [])
        .map((traveller: IQuotationTraveller) => {
            return {
                id: traveller.id,
            };
        })
        .sort((a: IQuotationTraveller, b: IQuotationTraveller) => a.id - b.id);
}

function extractValueHeaderTravellers(quotation: IQuotationsQuotation): IQuotationTraveller[] {
    return (quotation.travellers || [])
        .map((traveller: IQuotationTraveller) => {
            return {
                id: traveller.id,
                cost: traveller.cost
            };
        })
        .sort((a: IQuotationTraveller, b: IQuotationTraveller) => a.id - b.id);
}
